import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Browser } from "@capacitor/browser";
import { isSellerNativeApp } from "@/lib/native/runtime";
import {
  getNativeMetaStatus,
  clearNativeMetaAuthorization,
  readNativeMetaPending,
  subscribeNativeMeta,
} from "@/lib/native/meta-authorization";
import { openMetaAuthorization, type MetaIdentityProvider } from "./api";
import { awaitMetaStart } from "./authorization-start";

type AuthorizationResult = { authorizationUrl: string; nativeAttemptId?: string };

export function useMetaAuthorization() {
  const [openingProvider, setOpeningProvider] =
    useState<MetaIdentityProvider | null>(null);
  const [startingProvider, setStartingProvider] =
    useState<MetaIdentityProvider | null>(null);
  const starting = useRef<{
    provider: MetaIdentityProvider;
    revision: number;
    controller: AbortController;
  } | null>(null);
  const openingRevision = useRef(0);
  const finishWebOpening = useRef<(() => void) | null>(null);
  const mounted = useRef(true);
  const renderedRevision = openingRevision.current;
  const nativeStatus = useSyncExternalStore(
    subscribeNativeMeta,
    getNativeMetaStatus,
  );
  const nativePending = nativeStatus ? readNativeMetaPending() : null;
  useEffect(() => {
    mounted.current = true;
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) {
        openingRevision.current += 1;
        starting.current?.controller.abort();
        starting.current = null;
        setStartingProvider(null);
        finishWebOpening.current?.();
        setOpeningProvider(null);
      }
    };
    window.addEventListener("pageshow", restore);
    return () => {
      mounted.current = false;
      window.removeEventListener("pageshow", restore);
      openingRevision.current += 1;
      starting.current?.controller.abort();
      starting.current = null;
      finishWebOpening.current?.();
    };
  }, []);

  const open = async (
    provider: MetaIdentityProvider,
    result: AuthorizationResult,
  ) => {
    // Native status owns waiting and cancellation; web stays busy until navigation.
    const native = isSellerNativeApp();
    if (!mounted.current) return;
    finishWebOpening.current?.();
    const revision = ++openingRevision.current;
    try {
      if (native) {
        await openMetaAuthorization(
          result.authorizationUrl,
          provider,
          result.nativeAttemptId,
        );
        return;
      }
      setOpeningProvider(provider);
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        let timeout: number | undefined;
        const finish = (error?: unknown) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          window.removeEventListener("pagehide", departed);
          document.removeEventListener("visibilitychange", updateDeadline);
          if (finishWebOpening.current === departed)
            finishWebOpening.current = null;
          if (error !== undefined) reject(error);
          else resolve();
        };
        const departed = () => finish();
        const updateDeadline = () => {
          window.clearTimeout(timeout);
          if (
            settled ||
            openingRevision.current !== revision ||
            document.visibilityState === "hidden"
          ) return;
          timeout = window.setTimeout(() => {
            if (openingRevision.current !== revision) return finish();
            if (document.visibilityState === "hidden") return;
            finish(new Error("Authorization navigation did not complete"));
            window.stop();
          }, 30_000);
        };
        finishWebOpening.current = departed;
        window.addEventListener("pagehide", departed);
        document.addEventListener("visibilitychange", updateDeadline);
        updateDeadline();
        try {
          Promise.resolve(openMetaAuthorization(
            result.authorizationUrl,
            provider,
            result.nativeAttemptId,
          )).catch(finish);
        } catch (error) {
          finish(error);
        }
      });
    } catch (error) {
      if (openingRevision.current !== revision) return;
      setOpeningProvider(null);
      throw error;
    }
  };

  return {
    pendingProvider:
      startingProvider ??
      openingProvider ??
      (nativeStatus === "pending" ? nativePending?.provider : null),
    canCancel: Boolean(startingProvider || openingProvider || nativePending),
    cancel: (provider: MetaIdentityProvider) => {
      if (
        starting.current?.provider === provider &&
        starting.current.revision === renderedRevision
      ) {
        openingRevision.current += 1;
        starting.current.controller.abort();
        starting.current = null;
        setStartingProvider(null);
      } else if (
        openingProvider === provider &&
        openingRevision.current === renderedRevision
      ) {
        openingRevision.current += 1;
        finishWebOpening.current?.();
        window.stop();
        setOpeningProvider(null);
      } else if (
        nativePending?.provider === provider &&
        readNativeMetaPending()?.requestId === nativePending.requestId
      ) {
        openingRevision.current += 1;
        clearNativeMetaAuthorization(nativePending.requestId);
        void Browser.close().catch(() => undefined);
      }
    },
    start: async (
      provider: MetaIdentityProvider,
      operation: (signal: AbortSignal) => Promise<AuthorizationResult>,
    ) => {
      if (!mounted.current) return;
      const revision = ++openingRevision.current;
      starting.current?.controller.abort();
      finishWebOpening.current?.();
      const controller = new AbortController();
      starting.current = { provider, revision, controller };
      setStartingProvider(provider);
      setOpeningProvider(null);
      const timeout = window.setTimeout(
        () => controller.abort(new Error("Authorization start did not complete")),
        30_000,
      );
      let result: AuthorizationResult;
      try {
        result = await awaitMetaStart(
          () => operation(controller.signal),
          controller.signal,
        );
        if (openingRevision.current !== revision || !mounted.current) return;
      } catch (error) {
        controller.abort();
        if (openingRevision.current !== revision || !mounted.current) return;
        throw error;
      } finally {
        window.clearTimeout(timeout);
        if (starting.current?.revision === revision) {
          starting.current = null;
          setStartingProvider(null);
        }
      }
      await open(provider, result);
    },
    open,
  };
}
